// CANONICAL COPY (MNSA-01, the GH #7115 pattern). src/constants/ai/aiRiskProfiles.js re-exports this file so the browser and the
// Function App share one definition. Edit here, never there.
'use strict';

/**
 * src/constants/ai/aiRiskProfiles.js
 *
 * Built-in AI risk profiles for widely-used general-purpose AI tools.
 * These are static constants — NOT stored in tenant state. Tenant-specific
 * overrides are stored as deltas in state.aiRiskProfileOverrides[].
 *
 * Each profile carries a semver version. When a profile is updated, the
 * governance sweeper flags any linked AI system whose riskProfileVersion
 * differs with maturityFlag 'PROFILE_OUTDATED'.
 *
 * Default aiActClassification is always 'unclassified'. The system never
 * auto-classifies a tool as high-risk — that requires explicit human
 * confirmation (classificationConfirmedAt gate).
 *
 * riskDimensions are inherent risk signals about the tool's design and
 * data practices. They inform but never replace a human classification
 * decision.
 */

const AI_RISK_PROFILE_VERSION = '1.0.0';

const AI_RISK_PROFILES = [
  {
    id: 'profile-chatgpt',
    version: '1.0.0',
    name: 'ChatGPT',
    vendor: 'OpenAI',
    websiteUrl: 'https://openai.com',
    description:
      'Large language model assistant for text generation, summarisation, coding, and analysis.',
    deploymentTypes: ['saas', 'api'],
    defaultHostingRegion: 'us',
    dataProcessingAgreementAvailable: true,
    euDataResidencyAvailable: false,
    gdprArticle28Compliant: true,
    transferMechanism: 'scc',
    lastReviewedAt: '2025-01-01',
    source: 'OpenAI privacy policy, DPA, and EU AI Act conformity statement',
    riskDimensions: {
      dataRetentionRisk: 'medium', // prompts retained for training unless opted out
      thirdPartySubprocessors: 'medium', // Microsoft Azure hosting + others
      outputUnpredictability: 'medium',
      personalDataProcessing: 'possible',
      specialCategoryRisk: 'low', // no special-category focus by design
      transferOutsideEU: true,
      humanOversightMechanism: 'none-built-in',
    },
    defaultAiActClassification: 'unclassified',
    prohibitedPracticeRisk: 'low',
    annexIIICategories: [],
    recommendedControls: [
      'SCF-AIG-001', // AI governance policy
      'SCF-AIG-003', // AI risk assessment
      'SCF-AIG-007', // AI system monitoring
      'SCF-AIG-010', // Data quality for AI
      'SCF-PRI-001', // Privacy policy
    ],
    useCaseRiskSignals: {
      'hr-screening': 'high-risk-indicator',
      'credit-scoring': 'high-risk-indicator',
      'medical-advice': 'high-risk-indicator',
      'content-moderation': 'limited-risk-indicator',
      'text-summarisation': 'minimal-risk',
      'code-generation': 'minimal-risk',
      'customer-support': 'limited-risk-indicator',
    },
    notes:
      'EU API endpoint available (api.openai.com) but data may still route via US infrastructure. Review SCC Schedule C for data processing locations.',
  },
  {
    id: 'profile-claude',
    version: '1.0.0',
    name: 'Claude',
    vendor: 'Anthropic',
    websiteUrl: 'https://anthropic.com',
    description:
      'AI assistant focused on helpful, harmless, and honest text generation and analysis.',
    deploymentTypes: ['saas', 'api'],
    defaultHostingRegion: 'us',
    dataProcessingAgreementAvailable: true,
    euDataResidencyAvailable: false,
    gdprArticle28Compliant: true,
    transferMechanism: 'scc',
    lastReviewedAt: '2025-01-01',
    source: 'Anthropic privacy policy, DPA, and usage policies',
    riskDimensions: {
      dataRetentionRisk: 'low', // no training on API customer data by default
      thirdPartySubprocessors: 'low',
      outputUnpredictability: 'medium',
      personalDataProcessing: 'possible',
      specialCategoryRisk: 'low',
      transferOutsideEU: true,
      humanOversightMechanism: 'none-built-in',
    },
    defaultAiActClassification: 'unclassified',
    prohibitedPracticeRisk: 'low',
    annexIIICategories: [],
    recommendedControls: ['SCF-AIG-001', 'SCF-AIG-003', 'SCF-AIG-007', 'SCF-PRI-001'],
    useCaseRiskSignals: {
      'hr-screening': 'high-risk-indicator',
      'legal-research': 'limited-risk-indicator',
      'text-summarisation': 'minimal-risk',
      'code-generation': 'minimal-risk',
      'document-drafting': 'minimal-risk',
    },
    notes:
      'API data not used for training by default. Review Business Associates Agreement or DPA for your use case.',
  },
  {
    id: 'profile-microsoft-copilot',
    version: '1.0.0',
    name: 'Microsoft Copilot',
    vendor: 'Microsoft',
    websiteUrl: 'https://copilot.microsoft.com',
    description:
      'AI assistant integrated into Microsoft 365 and standalone Copilot products (Word, Excel, Teams, Bing).',
    deploymentTypes: ['saas'],
    defaultHostingRegion: 'multi-region',
    dataProcessingAgreementAvailable: true,
    euDataResidencyAvailable: true,
    gdprArticle28Compliant: true,
    transferMechanism: 'adequacy',
    lastReviewedAt: '2025-01-01',
    source: 'Microsoft Product Terms, DPA, and EU AI Act conformity statements',
    riskDimensions: {
      dataRetentionRisk: 'low', // M365 tenant data stays in tenant boundary
      thirdPartySubprocessors: 'low', // Microsoft internal infrastructure
      outputUnpredictability: 'medium',
      personalDataProcessing: 'high', // deep M365 integration means broad data access
      specialCategoryRisk: 'medium', // access to HR/finance data if not scoped
      transferOutsideEU: false, // EU data residency available
      humanOversightMechanism: 'partial',
    },
    defaultAiActClassification: 'unclassified',
    prohibitedPracticeRisk: 'low',
    annexIIICategories: [],
    recommendedControls: [
      'SCF-AIG-001',
      'SCF-AIG-003',
      'SCF-AIG-006', // AI transparency
      'SCF-AIG-007',
      'SCF-AIG-010',
      'SCF-IAM-001', // scope of access control
    ],
    useCaseRiskSignals: {
      'hr-screening': 'high-risk-indicator',
      'financial-analysis': 'limited-risk-indicator',
      'email-drafting': 'minimal-risk',
      'meeting-summarisation': 'minimal-risk',
      'document-drafting': 'minimal-risk',
    },
    notes:
      'EU data residency requires EU geo configuration in M365 admin centre. Copilot for M365 accesses all data the user has permission to read — scope review recommended.',
  },
  {
    id: 'profile-gemini',
    version: '1.0.0',
    name: 'Gemini',
    vendor: 'Google',
    websiteUrl: 'https://gemini.google.com',
    description:
      "Google's multimodal AI model available as consumer product and via Workspace/Vertex AI.",
    deploymentTypes: ['saas', 'api'],
    defaultHostingRegion: 'multi-region',
    dataProcessingAgreementAvailable: true,
    euDataResidencyAvailable: true,
    gdprArticle28Compliant: true,
    transferMechanism: 'adequacy',
    lastReviewedAt: '2025-01-01',
    source: 'Google Cloud DPA, Workspace DPA, and Vertex AI terms',
    riskDimensions: {
      dataRetentionRisk: 'medium', // consumer product; Workspace product lower
      thirdPartySubprocessors: 'medium',
      outputUnpredictability: 'medium',
      personalDataProcessing: 'possible',
      specialCategoryRisk: 'low',
      transferOutsideEU: false, // EU regions available via Vertex AI
      humanOversightMechanism: 'none-built-in',
    },
    defaultAiActClassification: 'unclassified',
    prohibitedPracticeRisk: 'low',
    annexIIICategories: [],
    recommendedControls: ['SCF-AIG-001', 'SCF-AIG-003', 'SCF-AIG-007', 'SCF-PRI-001'],
    useCaseRiskSignals: {
      'hr-screening': 'high-risk-indicator',
      'image-analysis': 'limited-risk-indicator',
      'text-summarisation': 'minimal-risk',
      'code-generation': 'minimal-risk',
    },
    notes:
      'Distinguish between consumer Gemini (higher data retention risk) and Workspace/Vertex AI (enterprise DPA, lower retention risk). Use Vertex AI for production workloads.',
  },
  {
    id: 'profile-mistral',
    version: '1.0.0',
    name: 'Mistral AI',
    vendor: 'Mistral AI',
    websiteUrl: 'https://mistral.ai',
    description: 'Open-weight and API language models from Mistral AI, a French AI company.',
    deploymentTypes: ['api', 'self-hosted'],
    defaultHostingRegion: 'eu',
    dataProcessingAgreementAvailable: true,
    euDataResidencyAvailable: true,
    gdprArticle28Compliant: true,
    transferMechanism: 'adequacy',
    lastReviewedAt: '2025-01-01',
    source: 'Mistral AI DPA and privacy policy',
    riskDimensions: {
      dataRetentionRisk: 'low', // EU-hosted, low retention by default
      thirdPartySubprocessors: 'low',
      outputUnpredictability: 'medium',
      personalDataProcessing: 'possible',
      specialCategoryRisk: 'low',
      transferOutsideEU: false,
      humanOversightMechanism: 'none-built-in',
    },
    defaultAiActClassification: 'unclassified',
    prohibitedPracticeRisk: 'low',
    annexIIICategories: [],
    recommendedControls: ['SCF-AIG-001', 'SCF-AIG-003', 'SCF-AIG-007'],
    useCaseRiskSignals: {
      'hr-screening': 'high-risk-indicator',
      'text-summarisation': 'minimal-risk',
      'code-generation': 'minimal-risk',
    },
    notes:
      'Open-weight models (Mistral 7B, Mixtral) can be self-hosted within the EU, eliminating data transfer concerns. API (la Plateforme) is EU-hosted. GPAI model provider under the EU AI Act — check GPAI obligations when using as a foundation model.',
  },
];

/** Look up a profile by id. Returns null if not found. */
function getAiRiskProfile(profileId) {
  return AI_RISK_PROFILES.find((p) => p.id === profileId) ?? null;
}

/**
 * Merge a built-in profile with a tenant override delta.
 * The delta is stored in state.aiRiskProfileOverrides[].overrideDelta
 * and only contains the fields the tenant has changed.
 */
function resolveAiProfile(profileId, overrides = []) {
  const base = getAiRiskProfile(profileId);
  if (!base) return null;
  const override = overrides.find((o) => o.profileId === profileId);
  if (!override?.overrideDelta) return base;
  return { ...base, ...override.overrideDelta };
}

/**
 * Check whether an AI system's linked profile is outdated.
 * Returns true when the system has a riskProfileId but its riskProfileVersion
 * no longer matches the current built-in version.
 */
function isProfileOutdated(aiSystem) {
  if (!aiSystem.riskProfileId || !aiSystem.riskProfileVersion) return false;
  const profile = getAiRiskProfile(aiSystem.riskProfileId);
  if (!profile) return false;
  return aiSystem.riskProfileVersion !== profile.version;
}

module.exports = {
  AI_RISK_PROFILE_VERSION,
  AI_RISK_PROFILES,
  getAiRiskProfile,
  resolveAiProfile,
  isProfileOutdated,
};
